import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";
import classNames from "classnames";

import PushableContainer from "./PushableContainer";
import { setStickyTitleBarHeight } from "../../reducers/styling";
import { useResponsiveFlag } from "./ResponsiveQueries";
import { useResizeObserver } from "./useResizeObserver";

const StickyTitleBar = props => {
  const dispatch = useDispatch();
  const { children } = props;

  const { isNonDesktop } = useResponsiveFlag();

  const [titleBar, setTitleBar] = useState();
  const titleBarHeight = useSelector(({ styling }) => styling.stickyTitleBarHeight);
  const affixHeaderOffsetTop = useSelector(({ styling }) => styling.affixHeaderOffsetTop);
  const complexMenuEnabled = useSelector(({ styling }) => styling.complexMenuEnabled);
  const complexSubMenuEnabled = useSelector(
    ({ styling }) => styling.complexSubMenuEnabled
  );

  const titleBarRefCallback = container => {
    if (!isEqual(container, titleBar)) setTitleBar(container);
  };

  const titleBarClassName = classNames("title-bar-sticky", props.className, {
    "title-bar-sticky-below-complex-menu": complexMenuEnabled && !complexSubMenuEnabled,
    "title-bar-sticky-below-complex-menu-sub-menu-open":
      complexMenuEnabled && complexSubMenuEnabled
  });

  const titleBarInnerClassName = classNames("padding-y-xs", props.innerClassName);
  const titleBarStyle =
    complexMenuEnabled && affixHeaderOffsetTop
      ? { ...props.style, top: affixHeaderOffsetTop }
      : props.style;

  const storeTitleBarHeight = useCallback(() => {
    const titleBarContent = titleBar?.childNodes[0];
    const height = titleBarContent ? titleBarContent.offsetHeight : 0;

    if (titleBarHeight === height) return;

    dispatch(setStickyTitleBarHeight(height));
  }, [dispatch, titleBar, titleBarHeight]);

  // Create an observer that will watch for the title bar resizes, which is triggered when
  // the "scrolled" class is added/removed on the "app" div container, and will update the
  // current titleBar height and the components that listen to its value.
  //
  // Note that this is needed because of the throttling of the "scrolled" class
  // adding/removing in the `App` component, which avoids some UI latency because of too
  // much scroll events processing.
  const resizeObserver = useResizeObserver(storeTitleBarHeight);

  useEffect(() => {
    if (!titleBar) return;

    storeTitleBarHeight();

    resizeObserver.observe(titleBar);
    window.addEventListener("scroll", storeTitleBarHeight);
    window.addEventListener("resize", storeTitleBarHeight);

    return () => {
      resizeObserver.unobserve(titleBar);
      window.removeEventListener("scroll", storeTitleBarHeight);
      window.removeEventListener("resize", storeTitleBarHeight);
    };
  }, [titleBar, storeTitleBarHeight, resizeObserver]);

  const preventPush = isNonDesktop && !complexMenuEnabled;

  return (
    <>
      <PushableContainer
        className="sticky-title-bar-placeholder"
        style={{ height: titleBarHeight || "auto" }}
      />

      <PushableContainer
        className={titleBarClassName}
        ref={titleBarRefCallback}
        preventPush={preventPush}
        style={titleBarStyle}
      >
        <div className={titleBarInnerClassName}>{children}</div>
      </PushableContainer>
    </>
  );
};

export default StickyTitleBar;
